/**
 * MediaGalleryTab Component
 * Manage agent media gallery (images, PDFs and audio) for send_media action
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  IconUpload,
  IconPhoto,
  IconFileTypePdf,
  IconTrash,
  IconCheck,
  IconMicrophone,
  IconPlayerStop,
  IconPlayerPlay,
  IconPencil,
  IconX,
  IconWand,
  IconInfoCircle,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import AgentsFlowService from '../../services/AgentsFlowService';
import { useAgentsFlow } from '../../contexts/AgentsFlowContext';
import ProBadge from '../ProBadge';
import useProFeature from '../../hooks/useProFeature';
import './MediaGalleryTab.css';

// ===============================================
// LIMITS
// ===============================================

const MEDIA_LIMITS = {
  image: {
    maxSize: 3 * 1024 * 1024, // 3MB
    maxCount: 10,
    label: 'Imagens',
    accept: 'image/jpeg,image/png,image/webp',
    extensions: 'JPEG, PNG, WEBP',
  },
  document: {
    maxSize: 15 * 1024 * 1024, // 15MB
    maxCount: 3,
    label: 'PDFs',
    accept: 'application/pdf',
    extensions: 'PDF',
  },
  audio: {
    maxSize: 2 * 1024 * 1024, // 2MB
    maxCount: 20,
    label: 'Audios',
    accept: 'audio/ogg,audio/mpeg,audio/wav,audio/webm,audio/mp4,audio/x-m4a',
    extensions: 'OGG, MP3, WAV, WebM, M4A',
  },
};

const ALL_ACCEPT = [
  MEDIA_LIMITS.image.accept,
  MEDIA_LIMITS.document.accept,
  MEDIA_LIMITS.audio.accept,
].join(',');

// ===============================================
// HELPERS
// ===============================================

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ===============================================
// MEDIA CARD
// ===============================================

const MediaCard = ({ media, onDelete, onUpdate }) => {
  const isImage = media.media_type === 'image';
  const isAudio = media.media_type === 'audio';
  const Icon = isImage ? IconPhoto : isAudio ? IconMicrophone : IconFileTypePdf;
  const badgeLabel = isImage ? 'Imagem' : isAudio ? 'Audio' : 'PDF';

  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(media.name);
  const [editDesc, setEditDesc] = useState(media.description || '');
  const [saving, setSaving] = useState(false);
  const nameInputRef = useRef(null);

  // Sync local state when media prop changes (after save)
  useEffect(() => {
    if (!editing) {
      setEditName(media.name);
      setEditDesc(media.description || '');
    }
  }, [media.name, media.description, editing]);

  const startEditing = () => {
    setEditName(media.name);
    setEditDesc(media.description || '');
    setEditing(true);
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const cancelEditing = () => {
    setEditName(media.name);
    setEditDesc(media.description || '');
    setEditing(false);
  };

  const saveEditing = async () => {
    const trimmedName = editName.trim();
    if (!trimmedName) return;

    const nameChanged = trimmedName !== media.name;
    const descChanged = editDesc.trim() !== (media.description || '');

    if (!nameChanged && !descChanged) {
      setEditing(false);
      return;
    }

    setSaving(true);
    try {
      const data = {};
      if (nameChanged) data.name = trimmedName;
      if (descChanged) data.description = editDesc.trim();
      await onUpdate(media.id, data);
      setEditing(false);
    } catch {
      // onUpdate already handles error
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEditing();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const colors = isImage
    ? { gradient: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)', color: '#db2777' }
    : isAudio
    ? { gradient: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', color: '#0284c7' }
    : { gradient: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', color: '#dc2626' };

  return (
    <div className={`mg-card ${editing ? 'mg-card--editing' : ''}`}>
      <div className="mg-card__left">
        {isImage && media.url ? (
          <div className="mg-card__thumbnail">
            <img src={media.url} alt={media.name} />
          </div>
        ) : (
          <div
            className="mg-card__icon"
            style={{ background: colors.gradient, color: colors.color }}
          >
            <Icon size={18} />
          </div>
        )}
        <div className="mg-card__info">
          {editing ? (
            <>
              <input
                ref={nameInputRef}
                className="mg-card__input mg-card__input--name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Nome da midia"
                maxLength={255}
                disabled={saving}
              />
              <input
                className="mg-card__input mg-card__input--desc"
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Adicione uma descricao..."
                maxLength={500}
                disabled={saving}
              />
            </>
          ) : (
            <>
              <div className="mg-card__header">
                <span className="mg-card__name">{media.name}</span>
                <span className={`mg-card__badge mg-card__badge--${media.media_type}`}>
                  {badgeLabel}
                </span>
              </div>
              {media.description ? (
                <span className="mg-card__description">{media.description}</span>
              ) : null}
            </>
          )}
          {isAudio && media.url ? (
            <audio controls preload="metadata" className="mg-card__audio">
              <source src={media.url} type={media.mimetype} />
            </audio>
          ) : !editing ? (
            <div className="mg-card__meta">
              <span>{formatFileSize(media.size)}</span>
              {media.original_filename && (
                <span>{media.original_filename}</span>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className="mg-card__actions">
        {editing ? (
          <>
            <button
              className="mg-card__btn mg-card__btn--save"
              onClick={saveEditing}
              disabled={saving || !editName.trim()}
              title="Salvar"
            >
              {saving ? <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} /> : <IconCheck size={15} />}
            </button>
            <button
              className="mg-card__btn"
              onClick={cancelEditing}
              disabled={saving}
              title="Cancelar"
            >
              <IconX size={15} />
            </button>
          </>
        ) : (
          <>
            <button
              className="mg-card__btn"
              onClick={startEditing}
              title="Editar nome e descricao"
            >
              <IconPencil size={15} />
            </button>
            <button
              className="mg-card__btn mg-card__btn--danger"
              onClick={() => onDelete(media)}
              title="Remover"
            >
              <IconTrash size={15} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ===============================================
// AUDIO RECORDER
// ===============================================

const MAX_RECORDING_SECONDS = 180; // 3 minutos

const AudioRecorder = ({ onSave, disabled, audioCount, agentId, llmProvider, showProBadge }) => {
  const [recState, setRecState] = useState('idle'); // idle | recording | preview
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioName, setAudioName] = useState('');
  const [audioDescription, setAudioDescription] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const stopRecordingRef = useRef(null);

  const supportsTranscription = llmProvider === 'openai' || llmProvider === 'google';

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // Keep ref updated for use inside setInterval
  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  const startRecording = async () => {
    if (audioCount >= MEDIA_LIMITS.audio.maxCount) {
      Swal.fire({
        icon: 'error',
        title: 'Limite atingido',
        text: `Maximo de ${MEDIA_LIMITS.audio.maxCount} audios por agente.`,
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setRecState('preview');
        stream.getTracks().forEach((t) => t.stop());
      };

      recorder.start();
      setRecState('recording');
      setElapsed(0);
      setAudioName('');
      setAudioDescription('');
      timerRef.current = setInterval(() => {
        setElapsed((p) => {
          const next = p + 1;
          if (next >= MAX_RECORDING_SECONDS) {
            stopRecordingRef.current?.();
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      console.error('Microphone error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Microfone indisponivel',
        text: 'Permita o acesso ao microfone no navegador.',
      });
    }
  };

  const discardRecording = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioName('');
    setAudioDescription('');
    setRecState('idle');
    setElapsed(0);
  };

  const handleTranscribe = async () => {
    if (!audioBlob || !agentId) return;
    setTranscribing(true);
    try {
      const file = new File([audioBlob], 'audio.webm', { type: 'audio/webm' });
      const response = await AgentsFlowService.transcribeAudio(agentId, file);
      const text = response.data?.transcription || '';
      if (text) {
        setAudioDescription(text);
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Transcricao vazia',
          text: 'Nao foi possivel extrair texto do audio. Descreva manualmente.',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error('Transcription error:', err);
      const errorMsg = err.response?.data?.error || 'Erro ao transcrever o audio.';
      Swal.fire({
        icon: 'error',
        title: 'Erro na transcricao',
        text: errorMsg,
      });
    } finally {
      setTranscribing(false);
    }
  };

  const saveRecording = () => {
    if (!audioBlob) return;

    if (!audioName.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Nome obrigatorio',
        text: 'Informe um nome para o audio antes de salvar.',
        timer: 2500,
        showConfirmButton: false,
      });
      return;
    }

    if (!audioDescription.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Descricao obrigatoria',
        text: 'Descreva o conteudo do audio para que o agente saiba o que ele contem.',
        timer: 2500,
        showConfirmButton: false,
      });
      return;
    }

    if (audioBlob.size > MEDIA_LIMITS.audio.maxSize) {
      Swal.fire({
        icon: 'error',
        title: 'Audio muito grande',
        text: `O tamanho maximo para audios e 5MB. Seu audio tem ${formatFileSize(audioBlob.size)}.`,
      });
      return;
    }

    const fileName = audioName.trim();
    const file = new File([audioBlob], `${fileName}.webm`, { type: 'audio/webm' });
    onSave(file, audioDescription.trim());
    discardRecording();
  };

  if (recState === 'recording') {
    return (
      <div className="mg-recorder mg-recorder--recording">
        <div className="mg-recorder__pulse" />
        <span className="mg-recorder__timer">{formatDuration(elapsed)}</span>
        <span className="mg-recorder__label">
          Gravando... {elapsed >= 160 && <span className="mg-recorder__time-warn">(max {formatDuration(MAX_RECORDING_SECONDS)})</span>}
        </span>
        <button className="mg-recorder__btn mg-recorder__btn--stop" onClick={stopRecording}>
          <IconPlayerStop size={16} />
          Parar
        </button>
      </div>
    );
  }

  if (recState === 'preview') {
    return (
      <div className="mg-recorder mg-recorder--preview">
        <audio controls src={audioUrl} preload="metadata" className="mg-recorder__player" />

        {/* Nome */}
        <div className="mg-recorder__field">
          <input
            className="mg-recorder__name-input"
            value={audioName}
            onChange={(e) => setAudioName(e.target.value)}
            placeholder="Nome do audio (ex: Explicacao corte a seco)"
            maxLength={255}
          />
          <span className="mg-recorder__field-hint">
            De um nome claro e descritivo. O agente usa o nome para identificar quando enviar este audio.
          </span>
        </div>

        {/* Descricao */}
        <div className="mg-recorder__field">
          <textarea
            className="mg-recorder__desc-input"
            value={audioDescription}
            onChange={(e) => setAudioDescription(e.target.value)}
            placeholder="Descreva o conteudo do audio..."
            maxLength={500}
            rows={3}
          />
          <span className="mg-recorder__field-hint">
            A descrição e essencial para que o agente saiba o conteúdo do audio e nao repita a informação por texto.
          </span>
        </div>

        {/* Transcrever (OpenAI/Google) */}
        {supportsTranscription && (
          <div className="mg-recorder__transcribe">
            <button
              className="mg-recorder__btn mg-recorder__btn--transcribe"
              onClick={handleTranscribe}
              disabled={transcribing}
            >
              {transcribing ? (
                <>
                  <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                  Transcrevendo...
                </>
              ) : (
                <>
                  <IconWand size={14} />
                  Transcrever audio automaticamente
                </>
              )}
            </button>
            <span className="mg-recorder__field-hint">
              Usa a IA do seu provedor para transcrever o audio e preencher a descricao automaticamente.
            </span>
          </div>
        )}

        {/* Aviso Anthropic */}
        {llmProvider === 'anthropic' && (
          <div className="mg-recorder__anthropic-notice">
            <IconInfoCircle size={14} />
            <span>O provedor Claude nao suporta transcricao automatica. Por favor, descreva manualmente o conteudo do audio para que o agente saiba o que ele contem.</span>
          </div>
        )}

        <div className="mg-recorder__preview-actions">
          <button className="mg-recorder__btn mg-recorder__btn--discard" onClick={discardRecording}>
            <IconTrash size={14} />
            Descartar
          </button>
          <button className="mg-recorder__btn mg-recorder__btn--save" onClick={saveRecording}>
            <IconCheck size={14} />
            Salvar
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      className="mg-recorder__start"
      onClick={startRecording}
      disabled={disabled || audioCount >= MEDIA_LIMITS.audio.maxCount}
    >
      <IconMicrophone size={16} />
      Gravar audio
      {showProBadge && <ProBadge small />}
    </button>
  );
};

// ===============================================
// UPLOAD ZONE
// ===============================================

const MediaUploadZone = ({ onUpload, uploading, imageCount, docCount, audioCount }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
      // Reset input so the same file can be re-selected
      e.target.value = '';
    }
  };

  const handleFiles = (files) => {
    const file = files[0];
    const isImage = file.type && file.type.startsWith('image');
    const isPdf = file.type === 'application/pdf';
    const isAudio = file.type && file.type.startsWith('audio');

    if (!isImage && !isPdf && !isAudio) {
      Swal.fire({
        icon: 'error',
        title: 'Tipo de arquivo nao suportado',
        text: 'Envie imagens (JPEG, PNG, WEBP), PDFs ou audios (OGG, MP3, WAV, WebM, M4A).',
      });
      return;
    }

    if (isImage) {
      if (file.size > MEDIA_LIMITS.image.maxSize) {
        Swal.fire({
          icon: 'error',
          title: 'Imagem muito grande',
          text: `O tamanho maximo para imagens e 3MB. Seu arquivo tem ${formatFileSize(file.size)}.`,
        });
        return;
      }
      if (imageCount >= MEDIA_LIMITS.image.maxCount) {
        Swal.fire({
          icon: 'error',
          title: 'Limite atingido',
          text: `Maximo de ${MEDIA_LIMITS.image.maxCount} imagens por agente.`,
        });
        return;
      }
    }

    if (isPdf) {
      if (file.size > MEDIA_LIMITS.document.maxSize) {
        Swal.fire({
          icon: 'error',
          title: 'PDF muito grande',
          text: `O tamanho maximo para PDFs e 15MB. Seu arquivo tem ${formatFileSize(file.size)}.`,
        });
        return;
      }
      if (docCount >= MEDIA_LIMITS.document.maxCount) {
        Swal.fire({
          icon: 'error',
          title: 'Limite atingido',
          text: `Maximo de ${MEDIA_LIMITS.document.maxCount} PDFs por agente.`,
        });
        return;
      }
    }

    if (isAudio) {
      if (file.size > MEDIA_LIMITS.audio.maxSize) {
        Swal.fire({
          icon: 'error',
          title: 'Audio muito grande',
          text: `O tamanho maximo para audios e 5MB. Seu arquivo tem ${formatFileSize(file.size)}.`,
        });
        return;
      }
      if (audioCount >= MEDIA_LIMITS.audio.maxCount) {
        Swal.fire({
          icon: 'error',
          title: 'Limite atingido',
          text: `Maximo de ${MEDIA_LIMITS.audio.maxCount} audios por agente.`,
        });
        return;
      }
    }

    onUpload(file);
  };

  const classes = [
    'mg-upload',
    dragActive && 'mg-upload--active',
    uploading && 'mg-upload--disabled',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={ALL_ACCEPT}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      {uploading ? (
        <>
          <Spinner animation="border" size="sm" className="mg-upload__spinner" />
          <p className="mg-upload__text">Enviando arquivo...</p>
        </>
      ) : (
        <>
          <IconUpload size={32} className="mg-upload__icon" />
          <p className="mg-upload__text">
            Arraste arquivos aqui ou clique para selecionar
          </p>
          <small className="mg-upload__hint">
            Imagens (JPEG, PNG, WEBP) ate 3MB | PDF ate 15MB | Audio ate 2MB
          </small>
        </>
      )}
    </div>
  );
};

// ===============================================
// UPLOAD FORM (name + description before upload)
// ===============================================

const getFileMediaType = (file) => {
  if (file.type && file.type.startsWith('image')) return 'image';
  if (file.type === 'application/pdf') return 'document';
  if (file.type && (file.type.startsWith('audio') || file.type === 'video/webm')) return 'audio';
  return 'unknown';
};

const MediaUploadForm = ({ file, onConfirm, onCancel, agentId, llmProvider }) => {
  const defaultName = file.name.replace(/\.[^/.]+$/, '');
  const [name, setName] = useState(defaultName);
  const [description, setDescription] = useState('');
  const [transcribing, setTranscribing] = useState(false);

  const mediaType = getFileMediaType(file);
  const isAudio = mediaType === 'audio';
  const supportsTranscription = isAudio && (llmProvider === 'openai' || llmProvider === 'google');

  const typeLabels = { image: 'Imagem', document: 'PDF', audio: 'Audio' };
  const typeIcons = { image: IconPhoto, document: IconFileTypePdf, audio: IconMicrophone };
  const TypeIcon = typeIcons[mediaType] || IconPhoto;

  const handleTranscribe = async () => {
    if (!file || !agentId) return;
    setTranscribing(true);
    try {
      const response = await AgentsFlowService.transcribeAudio(agentId, file);
      const text = response.data?.transcription || '';
      if (text) {
        setDescription(text);
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'Transcricao vazia',
          text: 'Nao foi possivel extrair texto do audio. Descreva manualmente.',
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error('Transcription error:', err);
      const errorMsg = err.response?.data?.error || 'Erro ao transcrever o audio.';
      Swal.fire({ icon: 'error', title: 'Erro na transcricao', text: errorMsg });
    } finally {
      setTranscribing(false);
    }
  };

  const handleConfirm = () => {
    if (!name.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Nome obrigatorio',
        text: 'Informe um nome para o arquivo antes de salvar.',
        timer: 2500,
        showConfirmButton: false,
      });
      return;
    }
    if (!description.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Descricao obrigatoria',
        text: 'Descreva o conteudo do arquivo para que o agente saiba o que ele contem.',
        timer: 2500,
        showConfirmButton: false,
      });
      return;
    }
    onConfirm(file, name.trim(), description.trim());
  };

  return (
    <div className="mg-upload-form">
      <div className="mg-upload-form__header">
        <TypeIcon size={16} />
        <span className="mg-upload-form__type">{typeLabels[mediaType] || 'Arquivo'}</span>
        <span className="mg-upload-form__filename">{file.name}</span>
        <span className="mg-upload-form__size">{formatFileSize(file.size)}</span>
      </div>

      {isAudio && (
        <audio controls preload="metadata" className="mg-recorder__player">
          <source src={URL.createObjectURL(file)} type={file.type} />
        </audio>
      )}

      {/* Nome */}
      <div className="mg-recorder__field">
        <input
          className="mg-recorder__name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={isAudio ? 'Nome do audio (ex: Explicacao corte a seco)' : 'Nome do arquivo'}
          maxLength={255}
        />
        <span className="mg-recorder__field-hint">
          De um nome claro e descritivo. O agente usa o nome para identificar quando enviar este arquivo.
        </span>
      </div>

      {/* Descricao */}
      <div className="mg-recorder__field">
        <textarea
          className="mg-recorder__desc-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descreva o conteudo do arquivo..."
          maxLength={500}
          rows={3}
        />
        <span className="mg-recorder__field-hint">
          A descricao e essencial para que o agente saiba o conteudo do arquivo e nao repita a informacao por texto.
        </span>
      </div>

      {/* Transcrever (apenas audio + OpenAI/Google) */}
      {supportsTranscription && (
        <div className="mg-recorder__transcribe">
          <button
            className="mg-recorder__btn mg-recorder__btn--transcribe"
            onClick={handleTranscribe}
            disabled={transcribing}
          >
            {transcribing ? (
              <>
                <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                Transcrevendo...
              </>
            ) : (
              <>
                <IconWand size={14} />
                Transcrever audio automaticamente
              </>
            )}
          </button>
          <span className="mg-recorder__field-hint">
            Usa a IA do seu provedor para transcrever o audio e preencher a descricao automaticamente.
          </span>
        </div>
      )}

      {/* Aviso Anthropic (apenas audio) */}
      {isAudio && llmProvider === 'anthropic' && (
        <div className="mg-recorder__anthropic-notice">
          <IconInfoCircle size={14} />
          <span>O provedor Claude nao suporta transcricao automatica. Por favor, descreva manualmente o conteudo do audio.</span>
        </div>
      )}

      <div className="mg-recorder__preview-actions">
        <button className="mg-recorder__btn mg-recorder__btn--discard" onClick={onCancel}>
          <IconX size={14} />
          Cancelar
        </button>
        <button className="mg-recorder__btn mg-recorder__btn--save" onClick={handleConfirm}>
          <IconCheck size={14} />
          Enviar
        </button>
      </div>
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const MediaGalleryTab = ({ agentId }) => {
  const { state } = useAgentsFlow();
  const llmProvider = state?.editor?.llm_config?.provider || 'openai';
  const { isProPlan } = useProFeature();

  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  // Load media on mount
  useEffect(() => {
    if (!agentId || agentId === 'novo') {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const loadMedia = async () => {
      try {
        const response = await AgentsFlowService.listMedia(agentId);
        if (!cancelled) {
          setMediaList(response.data || []);
        }
      } catch (error) {
        console.error('Error loading media:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadMedia();
    return () => { cancelled = true; };
  }, [agentId]);

  const imageCount = mediaList.filter((m) => m.media_type === 'image').length;
  const docCount = mediaList.filter((m) => m.media_type === 'document').length;
  const audioCount = mediaList.filter((m) => m.media_type === 'audio').length;

  // Called from MediaUploadForm (with name+description) or AudioRecorder
  const handleUpload = useCallback(async (file, name = '', description = '') => {
    setUploading(true);
    setPendingFile(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name || file.name.replace(/\.[^/.]+$/, ''));
      if (description) formData.append('description', description);

      const response = await AgentsFlowService.uploadMedia(agentId, formData);
      setMediaList((prev) => [response.data, ...prev]);

      Swal.fire({
        icon: 'success',
        title: 'Enviado!',
        text: `${name || file.name} adicionado a galeria.`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error uploading media:', error);
      const errorMsg = error.response?.data?.file?.[0]
        || error.response?.data?.detail
        || 'Erro ao enviar o arquivo.';
      Swal.fire({
        icon: 'error',
        title: 'Erro ao enviar',
        text: errorMsg,
      });
    } finally {
      setUploading(false);
    }
  }, [agentId]);

  // File selected from upload zone → show form instead of uploading immediately
  const handleFileSelected = useCallback((file) => {
    setPendingFile(file);
  }, []);

  const handleUpdate = useCallback(async (mediaId, data) => {
    try {
      const response = await AgentsFlowService.updateMedia(agentId, mediaId, data);
      setMediaList((prev) =>
        prev.map((m) => (m.id === mediaId ? { ...m, ...response.data } : m))
      );
    } catch (error) {
      console.error('Error updating media:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro ao salvar',
        text: 'Nao foi possivel atualizar a midia.',
        timer: 2000,
        showConfirmButton: false,
      });
      throw error;
    }
  }, [agentId]);

  const handleDelete = useCallback(async (media) => {
    const result = await Swal.fire({
      title: 'Remover midia?',
      text: `"${media.name}" sera removido permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      setMediaList((prev) => prev.filter((m) => m.id !== media.id));
      try {
        await AgentsFlowService.deleteMedia(agentId, media.id);
      } catch (error) {
        console.error('Error deleting media:', error);
        // Restore on error
        setMediaList((prev) => [...prev, media]);
      }
    }
  }, [agentId]);

  if (loading) {
    return (
      <div className="mg-config">
        <div className="mg-loading">
          <Spinner animation="border" size="sm" />
          <span>Carregando galeria...</span>
        </div>
      </div>
    );
  }

  if (!agentId || agentId === 'novo') {
    return (
      <div className="mg-config">
        <div className="mg-empty">
          <IconPhoto size={40} className="mg-empty__icon" />
          <p className="mg-empty__text">Salve o agente primeiro</p>
          <p className="mg-empty__subtitle">
            A galeria de midias estara disponivel apos salvar o agente.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mg-config">
      {/* Stats Bar */}
      <div className="mg-stats">
        <span className="mg-stats__item">
          <IconPhoto size={14} />
          <span className="mg-stats__value">{imageCount}</span>/{MEDIA_LIMITS.image.maxCount} imagens
        </span>
        <span className="mg-stats__item">
          <IconFileTypePdf size={14} />
          <span className="mg-stats__value">{docCount}</span>/{MEDIA_LIMITS.document.maxCount} PDFs
        </span>
        <span className="mg-stats__item" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <IconMicrophone size={14} />
          <span className="mg-stats__value">{audioCount}</span>/{MEDIA_LIMITS.audio.maxCount} audios
          {!isProPlan && <ProBadge small />}
        </span>
        {mediaList.length > 0 && (
          <span className="mg-stats__badge">
            <IconCheck size={10} />
            {mediaList.length} {mediaList.length === 1 ? 'arquivo' : 'arquivos'}
          </span>
        )}
      </div>

      {/* Upload Zone */}
      <MediaUploadZone
        onUpload={handleFileSelected}
        uploading={uploading || !!pendingFile}
        imageCount={imageCount}
        docCount={docCount}
        audioCount={audioCount}
      />

      {/* Pending file form (name + description before upload) */}
      {pendingFile && (
        <MediaUploadForm
          file={pendingFile}
          onConfirm={(file, name, description) => handleUpload(file, name, description)}
          onCancel={() => setPendingFile(null)}
          agentId={agentId}
          llmProvider={llmProvider}
        />
      )}

      {/* Audio Recorder */}
      <AudioRecorder
        onSave={(file, description) => handleUpload(file, file.name.replace(/\.[^/.]+$/, ''), description)}
        disabled={uploading || !!pendingFile}
        audioCount={audioCount}
        agentId={agentId}
        llmProvider={llmProvider}
        showProBadge={!isProPlan}
      />

      {/* Media List */}
      {mediaList.length > 0 ? (
        <div className="mg-list">
          {mediaList.map((media) => (
            <MediaCard
              key={media.id}
              media={media}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      ) : (
        <div className="mg-empty">
          <IconPhoto size={40} className="mg-empty__icon" />
          <p className="mg-empty__text">Nenhuma midia adicionada.</p>
          <p className="mg-empty__subtitle">
            Envie imagens, PDFs ou audios para usar na acao "Enviar Midia" do fluxo.
          </p>
        </div>
      )}
    </div>
  );
};

export default MediaGalleryTab;
